import type { UppFloorPlanStageComponent } from '../canvas/upp-floor-plan-stage.component';
import type { UppKonvaFloorPlanModel } from '../internal/upp-konva-floor-plan-model';
import { bindUppKonvaStageFloor } from '../internal/upp-konva-stage-host-bridge';
import { uppKonvaSceneModelHost } from '../internal/upp-konva-scene-model-host';

/**
 * Context of callbacks that the host component **`upp-konva-floor-plan`** supplies when wiring the model.
 */
export interface UppKonvaHostModelWireContext {
  /** English comment normalized. */
  floor: UppFloorPlanStageComponent | undefined;

  /** After structural change of **`shapes`** (after the previous user callback **`onModelChanged`**). */
  emitStructuralAreaToHost: () => void;

  /** English comment normalized. */
  onPresentationRefresh: () => void;

  /** From **`bindFloorRefresh`**: synchronize snapshots and {@link UppFloorPlanStageComponent.refreshFromModel}. */
  onFloorModelRefresh: (floor: UppFloorPlanStageComponent) => void;

  /** English comment normalized. */
  onAfterFloorWired: () => void;
}

/**
 * Integration layer for wiring the model to the host component callbacks.
 */
export class UppKonvaHostModelIntegration {
  private _hooksModel: UppKonvaFloorPlanModel | null = null;
  private _userOnModelChanged: (() => void) | null = null;

  /** English comment normalized. */
  get hookedModel(): UppKonvaFloorPlanModel | null {
    return this._hooksModel;
  }

  wire(model: UppKonvaFloorPlanModel | undefined, context: UppKonvaHostModelWireContext): void {
    if (!model) {
      return;
    }
    const m = model;
    if (this._hooksModel !== m) {
      this._hooksModel = m;
      this._userOnModelChanged = m.onModelChanged;
      m.onModelChanged = () => {
        this._userOnModelChanged?.();
        context.emitStructuralAreaToHost();
      };
      uppKonvaSceneModelHost(m).onRefresh(() => {
        context.onPresentationRefresh();
      });
    }
    const floor = context.floor;
    if (floor) {
      uppKonvaSceneModelHost(m).bindFloorRefresh(() => {
        context.onFloorModelRefresh(floor);
      });
      uppKonvaSceneModelHost(m).bindExportToPng((opts) => floor.exportToPng(opts));
      bindUppKonvaStageFloor(m, floor);
      context.onAfterFloorWired();
    } else {
      uppKonvaSceneModelHost(m).bindExportToPng(null);
      bindUppKonvaStageFloor(m, null);
    }
  }

  teardown(m: UppKonvaFloorPlanModel): void {
    if (this._hooksModel !== m) {
      return;
    }
    m.onModelChanged = this._userOnModelChanged;
    const h = uppKonvaSceneModelHost(m);
    h.onRefresh(null);
    h.bindFloorRefresh(null);
    h.bindExportToPng(null);
    bindUppKonvaStageFloor(m, null);
    this._hooksModel = null;
    this._userOnModelChanged = null;
  }
}
